import { BreakEvenPointDto } from "#interfaces/executive-dashboard/break-even-point.interface";
import { PendingPremiumSummaryDto } from "#interfaces/executive-dashboard/pending-premium-summary.interface";
import { ProjectAreaSummaryDto } from "#interfaces/executive-dashboard/project-area-summary-interface";
import { ProjectIncomeSummaryDto } from "#interfaces/executive-dashboard/project-income-summary.interface";
import { ProjectReservedIncomeSummary } from "#interfaces/executive-dashboard/project-reserve-income-summary.interface";
import { ProjectSubcategoriesSummaryDto } from "#interfaces/executive-dashboard/project-subcategory-summary.interface";
import { ApiResponseData } from "#interfaces/http-response/responses/http-responses";
import { cleanParams } from "#utils/clean-params/clean-params.helper";
import { HeadersHttpService } from "#utils/http-headers/http-headers.service";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { BehaviorSubject, Observable, catchError, tap, throwError } from "rxjs";
import { ExecutiveDashboardService } from "./executive-dashboard.interface";

@Injectable({
  providedIn: "root",
})
export class ExecutiveDashboardServiceImpl
  implements ExecutiveDashboardService
{
  baseUrl: string = `${environment.baseUrl}ExecutiveDashboard/`;

  // BEHAVIOR SUBJECT

  _projectIncomeSummary: BehaviorSubject<
    ApiResponseData<ProjectIncomeSummaryDto>
  > = new BehaviorSubject(null);
  _pendingPremiumSummary: BehaviorSubject<
    ApiResponseData<PendingPremiumSummaryDto>
  > = new BehaviorSubject(null);
  _breakEvenPointSummary: BehaviorSubject<ApiResponseData<BreakEvenPointDto>> =
    new BehaviorSubject(null);
  _projectReservedIncomeSummary: BehaviorSubject<
    ApiResponseData<ProjectReservedIncomeSummary>
  > = new BehaviorSubject(null);
  _projectSubcategoriesSummary: BehaviorSubject<
    ApiResponseData<ProjectSubcategoriesSummaryDto>
  > = new BehaviorSubject(null);
  _projectAreaSummary: BehaviorSubject<ApiResponseData<ProjectAreaSummaryDto>> =
    new BehaviorSubject(null);

  // OBSERVABLE

  projectIncomeSummary$: Observable<ApiResponseData<ProjectIncomeSummaryDto>> =
    this._projectIncomeSummary.asObservable();
  pendingPremiumSummary$: Observable<
    ApiResponseData<PendingPremiumSummaryDto>
  > = this._pendingPremiumSummary.asObservable();
  breakEvenPointSummary$: Observable<ApiResponseData<BreakEvenPointDto>> =
    this._breakEvenPointSummary.asObservable();
  projectReservedIncomeSummary$: Observable<
    ApiResponseData<ProjectReservedIncomeSummary>
  > = this._projectReservedIncomeSummary.asObservable();
  projectSubcategoriesSummary$: Observable<
    ApiResponseData<ProjectSubcategoriesSummaryDto>
  > = this._projectSubcategoriesSummary.asObservable();
  projectAreaSummary$: Observable<ApiResponseData<ProjectAreaSummaryDto>> =
    this._projectAreaSummary.asObservable();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly httpHeaderService: HeadersHttpService
  ) {}

  ProjectIncomeSummary(
    payload: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = { projectId: null, initDate: null, endDate: null }
  ): Observable<ApiResponseData<ProjectIncomeSummaryDto>> {
    const { projectId = null, initDate = null, endDate = null } = payload;

    const params = cleanParams({ projectId, initDate, endDate });

    return this.httpClient
      .get<ApiResponseData<ProjectIncomeSummaryDto>>(
        `${this.baseUrl}ProjectIncomeSummary`,
        {
          params,
          headers: this.httpHeaderService.getHeadersPrivate(),
        }
      )
     .pipe(
    tap((response) => this._projectIncomeSummary.next(response)),
    catchError((error: HttpErrorResponse) => {
      this._projectIncomeSummary.next({
        succeeded: false,
        message: error?.error?.Message ?? error?.error?.message ?? 'Error al obtener los datos.',
        errors: null, data: null
      });
      return throwError(() => error);
    })
  );
}
  PendingPremiumAmountReceivableSummary(payload: {
    projectId: number | null;
    closingDate: string;
  }): Observable<ApiResponseData<PendingPremiumSummaryDto>> {
    return this.httpClient
      .get<ApiResponseData<PendingPremiumSummaryDto>>(
        `${this.baseUrl}PendingPremiumAmountReceivableSummary`,
        {
          params: {
            projectId: payload.projectId,
            closingDate: payload.closingDate,
          },
          headers: this.httpHeaderService.getHeadersPrivate(),
        }
      )
 .pipe(
    tap((response) => this._pendingPremiumSummary.next(response)),
    catchError((error: HttpErrorResponse) => {
      this._pendingPremiumSummary.next({
        succeeded: false,
        message: error?.error?.Message ?? error?.error?.message ?? 'Error al obtener los datos.',
        errors: null, data: null
      });
      return throwError(() => error);
    })
  );
}

  BreakEvenPointSummary(
    payload: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = { projectId: null, initDate: null, endDate: null }
  ): Observable<ApiResponseData<BreakEvenPointDto>> {
    const { projectId = null, initDate = null, endDate = null } = payload;

    const params = cleanParams({ projectId, initDate, endDate });

    return this.httpClient
      .get<ApiResponseData<BreakEvenPointDto>>(
        `${this.baseUrl}BreakEvenPointSummary`,
        {
          params,
          headers: this.httpHeaderService.getHeadersPrivate(),
        }
      )
      .pipe(
    tap((response) => this._breakEvenPointSummary.next(response)),
    catchError((error: HttpErrorResponse) => {
      this._breakEvenPointSummary.next({
        succeeded: false,
        message: error?.error?.Message ?? error?.error?.message ?? 'Error al obtener los datos.',
        errors: null, data: null
      });
      return throwError(() => error);
    })
  );
}

  ProjectReservedIncomeSummary(
    payload: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = { projectId: null, initDate: null, endDate: null }
  ): Observable<ApiResponseData<ProjectReservedIncomeSummary>> {
    const { projectId = null, initDate = null, endDate = null } = payload;

    const params = cleanParams({ projectId, initDate, endDate });

    return this.httpClient
      .get<ApiResponseData<ProjectReservedIncomeSummary>>(
        `${this.baseUrl}ProjectReservedIncomeSummary`,
        {
          params,
          headers: this.httpHeaderService.getHeadersPrivate(),
        }
      )
      .pipe(
    tap((response) => this._projectReservedIncomeSummary.next(response)),
    catchError((error: HttpErrorResponse) => {
      this._projectReservedIncomeSummary.next({
        succeeded: false,
        message: error?.error?.Message ?? error?.error?.message ?? 'Error al obtener los datos.',
        errors: null, data: null
      });
      return throwError(() => error);
    })
  );
}

  ProjectSubcategoriesSummaryKpi(
    payload: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = { projectId: null, initDate: null, endDate: null }
  ): Observable<ApiResponseData<ProjectSubcategoriesSummaryDto>> {
    const { projectId = null, initDate = null, endDate = null } = payload;

    const params = cleanParams({ projectId, initDate, endDate });

    return this.httpClient
      .get<ApiResponseData<ProjectSubcategoriesSummaryDto>>(
        `${this.baseUrl}ProjectSubcategoriesSummaryKpi`,
        {
          params,
          headers: this.httpHeaderService.getHeadersPrivate(),
        }
      )
       .pipe(
      tap((response) => {
        this._projectSubcategoriesSummary.next(response);
      }),
      catchError((error: HttpErrorResponse) => {
        this._projectSubcategoriesSummary.next({
          succeeded: false,
          message: error?.error?.Message ?? error?.error?.message ?? 'Error al obtener los datos.',
          errors: null,
          data: null
        });
        return throwError(() => error);
      })
    );
  }

  ProjectAreaSummary(
    payload: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = { projectId: null, initDate: null, endDate: null }
  ): Observable<ApiResponseData<ProjectAreaSummaryDto>> {
    const { projectId = null, initDate = null, endDate = null } = payload;

    const params = cleanParams({ projectId, initDate, endDate });

    return this.httpClient
      .get<ApiResponseData<ProjectAreaSummaryDto>>(
        `${this.baseUrl}GetProjectAreaSummary`,
        {
          params,
          headers: this.httpHeaderService.getHeadersPrivate(),
        }
      )
      .pipe(
        tap((response) => {
          this._projectAreaSummary.next(response);
        }),
  catchError((error: HttpErrorResponse) => {
        this._projectAreaSummary.next({
          succeeded: false,
    message: error?.error?.Message ?? error?.error?.message ?? 'Error al obtener los datos.',
          errors: null,
          data: null
        });
        return throwError(() => error);
      } )
      );
  }

}
